import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { PartnerMember, PartnerResource } from "../../api/partners";
import { useDialogState } from "../../hooks/useDialogState";
import { useSnackbar } from "../../hooks/useSnackbar";
import { BulkImportDialog } from "./BulkImportDialog";

interface PartnerMembersDialogProps {
  open: boolean;
  partnerId: string;
  partnerName: string;
  onClose: () => void;
}

export const PartnerMembersDialog: React.FC<PartnerMembersDialogProps> = ({
  open,
  partnerId,
  partnerName,
  onClose,
}) => {
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const bulkDialog = useDialogState();

  const [members, setMembers] = React.useState<PartnerMember[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [adding, setAdding] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string>();

  const loadMembers = React.useCallback(async () => {
    setLoading(true);
    try {
      setMembers(await PartnerResource.listMembers(partnerId));
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to load members.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [partnerId, showSnackbar]);

  React.useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [open, loadMembers]);

  const handleAdd = async () => {
    if (!email.trim() || !displayName.trim()) return;
    setAdding(true);
    try {
      const result = await PartnerResource.addMember(partnerId, {
        email: email.trim(),
        displayName: displayName.trim(),
      });
      const note = result.alreadyHadActiveSubscription
        ? " (already had active subscription — promo skipped)"
        : "";
      showSnackbar(`Member ${result.status}${note}.`);
      setEmail("");
      setDisplayName("");
      await loadMembers();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to add member.",
        "error"
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member: PartnerMember) => {
    setRemovingId(member.appUserId);
    try {
      await PartnerResource.removeMember(partnerId, {
        appUserId: member.appUserId,
      });
      showSnackbar(`${member.email} offboarded.`);
      setMembers((prev) =>
        prev.filter((m) => m.appUserId !== member.appUserId)
      );
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to offboard member.",
        "error"
      );
    } finally {
      setRemovingId(undefined);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <span>Members — {partnerName}</span>
          <Button
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={bulkDialog.openDialog}
          >
            Bulk Import
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {/* Add member */}
          <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
            <TextField
              size="small"
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              size="small"
              label="Display Name"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={
                adding ? <CircularProgress size={16} /> : <PersonAddIcon />
              }
              onClick={handleAdd}
              disabled={adding || !email.trim() || !displayName.trim()}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Add
            </Button>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {loading ? "Loading…" : `Members (${members.length})`}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : members.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ py: 2, textAlign: "center" }}
            >
              No members yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => {
                  const isRemoving = removingId === member.appUserId;
                  return (
                    <TableRow key={member.appUserId}>
                      <TableCell>{member.displayName || "—"}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Offboard from partner">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isRemoving}
                              onClick={() => handleRemove(member)}
                            >
                              {isRemoving ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <BulkImportDialog
        open={bulkDialog.open}
        partnerId={partnerId}
        partnerName={partnerName}
        onClose={bulkDialog.close}
        onImported={loadMembers}
      />

      {SnackbarNode}
    </>
  );
};
